import type { ButtonHTMLAttributes, InputHTMLAttributes, JSX, ReactNode } from 'react';
import { Logo } from './Logo';

/*
 * Bausteine nach dem goEducation UI Kit.
 *
 * Sie stehen hier zusammen, damit sich die Gestaltung an einer Stelle aendern
 * laesst und nicht in zwoelf Komponenten verstreut als Klassenkette lebt. Die
 * Werte sind aus dem Kit ausgelesen: Radius 10 px an Knoepfen, 12 px an Karten,
 * Kanten statt Schatten, Lime als einzige Handlungsfarbe.
 */

export function Card({
  children,
  className = '',
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-xl border border-border bg-bg shadow-xs ${padded ? 'p-4 sm:p-5' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export function SectionTitle({
  children,
  right,
}: {
  children: ReactNode;
  right?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="text-base text-fg">{children}</h2>
      {right !== undefined && <span className="text-sm text-fg-muted">{right}</span>}
    </div>
  );
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<Variant, string> = {
  // Lime ist im Kit die einzige Handlungsfarbe. Sparsam einsetzen, sonst
  // verliert sie genau die Wirkung, wegen der sie da ist.
  primary: 'bg-brand text-fg hover:bg-brand-600 disabled:bg-bg-muted disabled:text-fg-muted',
  secondary:
    'border border-border bg-bg text-fg-subtle hover:bg-bg-muted hover:text-fg disabled:text-fg-muted',
  ghost: 'text-fg-subtle hover:bg-bg-muted hover:text-fg disabled:text-fg-muted',
  danger:
    'border border-danger/35 text-danger hover:bg-danger/10 disabled:border-border disabled:text-fg-muted',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: 'sm' | 'md';
}): JSX.Element {
  /*
   * min-h-11 sind 44 px — die Groesse, die Apple fuer Beruehrungsziele nennt.
   * Die Oberflaeche wird ausdruecklich auch vom Handy und iPad bedient; ohne das
   * waeren die Knoepfe 38 px hoch und man trifft daneben.
   */
  const sizing =
    size === 'sm'
      ? 'rounded-lg px-3 py-1.5 text-xs'
      : 'min-h-11 rounded-lg px-4 py-2 text-sm';
  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 font-medium transition-colors disabled:cursor-not-allowed ${sizing} ${VARIANTS[variant]} ${className}`}
      {...rest}
    />
  );
}

export function Input({
  className = '',
  ...rest
}: InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return (
    <input
      className={`w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-muted focus:border-fg focus:outline-none disabled:bg-bg-muted ${className}`}
      {...rest}
    />
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-fg">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {/* Fehler verdraengt den Hinweis: beides zugleich lenkt vom Problem ab. */}
      {error != null && error !== '' ? (
        <p className="mt-1.5 text-xs text-danger">{error}</p>
      ) : hint !== undefined ? (
        <p className="mt-1.5 text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  );
}

export type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'muted';

const TONES: Record<Tone, string> = {
  neutral: 'bg-bg-muted text-fg-subtle',
  brand: 'bg-brand text-fg',
  success: 'bg-success/12 text-[color:#3d7c3f]',
  warning: 'bg-warning/15 text-[color:#8a5a00]',
  danger: 'bg-danger/12 text-[color:#c22]',
  muted: 'bg-bg-muted text-fg-muted',
};

/** Pille mit Punkt. Der Punkt pulst, solange etwas in Bewegung ist. */
export function Pill({
  tone = 'neutral',
  pulsing = false,
  children,
  title,
}: {
  tone?: Tone;
  pulsing?: boolean;
  children: ReactNode;
  title?: string;
}): JSX.Element {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${TONES[tone]}`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full bg-current ${pulsing ? 'animate-pulse' : ''}`}
      />
      {children}
    </span>
  );
}

/**
 * Kennzahl im Stil der Kachelreihe des Kits: grosse Zahl, kleine Beschriftung
 * in Versalien darunter.
 */
export function Stat({
  value,
  label,
  tone = 'neutral',
}: {
  value: ReactNode;
  label: string;
  tone?: 'neutral' | 'danger';
}): JSX.Element {
  return (
    <div className="px-4 py-3.5 sm:px-5">
      <p
        className={`tabular text-2xl leading-8 font-medium tracking-tight ${tone === 'danger' ? 'text-danger' : 'text-fg'}`}
      >
        {value}
      </p>
      <p className="mt-0.5 text-[11px] tracking-wide text-fg-muted uppercase">{label}</p>
    </div>
  );
}

/** Die goAVA-Wortmarke. Hoehe ueber die Klasse steuern, Farbe erbt sie. */
export function Wordmark({ className = 'h-6' }: { className?: string }): JSX.Element {
  return <Logo className={`${className} w-auto text-fg`} />;
}
